"use client";

import * as React from "react";

import { howtoStorageKey, writeHowtoDismissedCookie } from "../_lib/howto-cookie";

/**
 * The one piece of state the header "?" button and the page-slot how-to card
 * share. The provider renders NO DOM — it wraps the page's header + <main>
 * inside SupplyShell so both ends of the contract read the same store.
 *
 * `dismissed` is persisted (cookie + localStorage, see ../_lib/howto-cookie.ts)
 * through `useSyncExternalStore`, whose server snapshot is the cookie value
 * the page read — SSR and hydration paint the same thing, and a dismissed
 * user never sees the card flash open. `forceOpen` is session-only: the
 * header "?" reopens the card for this mount without un-dismissing it.
 *
 * `open = forceOpen || !dismissed`. Closing from the header while not yet
 * dismissed IS a dismissal — a non-persisting close would resurrect the card
 * on every reload for first-time users, which is the defect this replaces.
 */
export type HowtoStore = {
  surface: string;
  open: boolean;
  dismissed: boolean;
  toggle: () => void;
  dismiss: () => void;
};

const HowtoContext = React.createContext<HowtoStore | null>(null);

/** null when no provider wraps the caller (a surface without a how-to). */
export function useHowto(): HowtoStore | null {
  return React.useContext(HowtoContext);
}

export function HowtoProvider(props: {
  surface: string;
  initiallyDismissed: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  const { surface, initiallyDismissed, children } = props;
  const storageKey = howtoStorageKey(surface);
  const listenersRef = React.useRef(new Set<() => void>());
  // Tab-lifetime fallback for private mode / wedged quota: without it a
  // blocked `setItem` leaves "Faham" reading as dead.
  const sessionDismissedRef = React.useRef(false);

  const subscribe = React.useCallback((listener: () => void) => {
    const listeners = listenersRef.current;
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  const getSnapshot = React.useCallback(() => {
    if (initiallyDismissed || sessionDismissedRef.current) return true;
    try {
      return localStorage.getItem(storageKey) === "dismissed";
    } catch {
      return false;
    }
  }, [initiallyDismissed, storageKey]);

  const getServerSnapshot = React.useCallback(() => initiallyDismissed, [initiallyDismissed]);

  const dismissed = React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Legacy convergence: a user who tapped Faham before the cookie existed
  // carries storage=dismissed and no cookie. The card stays closed for them
  // client-side, so they would never tap Faham again and the SSR flash would
  // persist forever. Mirror the flag forward once.
  React.useEffect(() => {
    if (initiallyDismissed) return;
    try {
      if (localStorage.getItem(storageKey) === "dismissed") writeHowtoDismissedCookie(surface);
    } catch {
      // Storage unreadable — nothing to mirror.
    }
  }, [initiallyDismissed, storageKey, surface]);

  const [forceOpen, setForceOpen] = React.useState(false);
  const open = forceOpen || !dismissed;

  const dismiss = React.useCallback(() => {
    sessionDismissedRef.current = true;
    try {
      localStorage.setItem(storageKey, "dismissed");
    } catch {
      // Write-blocked — the session flag holds the collapse for this tab.
    }
    try {
      writeHowtoDismissedCookie(surface);
    } catch {
      // No document — storage / session flag still hold.
    }
    setForceOpen(false);
    listenersRef.current.forEach((listener) => listener());
  }, [storageKey, surface]);

  const toggle = React.useCallback(() => {
    if (!open) {
      setForceOpen(true);
      return;
    }
    if (!dismissed) dismiss();
    else setForceOpen(false);
  }, [open, dismissed, dismiss]);

  const value = React.useMemo<HowtoStore>(
    () => ({ surface, open, dismissed, toggle, dismiss }),
    [surface, open, dismissed, toggle, dismiss],
  );

  return <HowtoContext.Provider value={value}>{children}</HowtoContext.Provider>;
}
